import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import classnames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useHistory } from "react-router-dom";

import {
	getNextSearchResultsPage, getSearchPage,
	clearRedirectTo
} from "store/modules/search";
import { searchPageSelector } from "store/selectors/SearchPage";

import { Section, Container, Page } from "components/core/Page";
import { Paragraph, Text } from "components/core/Typography";
import { ScrollToTop } from "components/core/ScrollToTop";
import { Breadcrumbs } from "components/search/Breadcrumbs";
import { SearchBar } from "components/search/SearchBar";
import SisterPages from "components/search/SearchLandingContent/SisterPages";
import NearbyCities from "components/search/SearchLandingContent/NearbyCities";

import { FILTER_GROUPS } from "config/filters";
import useSearchFilters from "hooks/search/useSearchFilters";
import useActiveFilterCount from "hooks/search/useActiveFilterCount";
import { decodeQueryStringValue } from "utils/search/buildSearchLink";
import { getDisplayCityName } from "utils/location";
import { getSearchPageSyncTargetPath } from "utils/search/searchPageUrlSync";
import navigate from "utils/navigate";

import Row from "components/core/Page/Row";
import Column from "components/core/Page/Column";
import SearchResults from "components/organisms/search/SearchResults";
import { Button } from "components/core/Button";
import { LoadingBoxes } from "components/core/Icons";
import { HTMLContent } from "components/core/HTMLContent";

import { generateCLPWebPageSchema, generateCLPWebsiteSchema, organizationSchema } from "config/schemas";
import createSelfStorageSchema from "@sparefoot/react-commons/utils/createSelfStorageSchema";
import { mapAmenitiesToFeature } from "config/amenityMap";

import usePageViewTracking from "hooks/tracking/usePageViewTracking";
import { PAGE_TYPES, PAGE_SUB_TYPES } from "@sparefoot/tracking-events";

import SearchPageContext from "pages/SearchPageV1/SearchPageV1Context";
import ClickToCallTray from "components/organisms/search/ClickToCallTray/ClickToCallTray";
import StickToolBar from "components/organisms/search/StickToolBar/StickToolBar";

import { combineSchemas } from "./utils/combineSchemas";

import "./SearchPageV2.scss";

import useMapSearch from "./useMapSearch";
import SearchPageV2MapColumn from "./SearchPageV2MapColumn";


export default function SearchPageV2() {
	const {
		cityUrl,
		meta,
		pageText = "",
		pageType,
		location,
		stateUrl,
		currentPage,
		hasMorePages,
		totalListings,
		nearbyCities,
		amenityPages,
		facilityCards,
		isCallCenterOpen
	} = useSelector(searchPageSelector);
	const isSearchLoading = useSelector((reduxState) => reduxState?.search?.loading);
	const baseUrl = useSelector((reduxState) => reduxState?.app?.baseUrl);
	const redirectTo = useSelector((reduxState) => reduxState.search.redirectTo);
	const dispatch = useDispatch();
	// Guard when location is empty (e.g. invalid API response or initial load) to avoid undefined/map crash
	const state = location?.state ?? "";
	const preferredPlaceName = location?.preferredPlaceName ?? "";
	const city = location?.city ?? "";
	const { search, pathname } = useLocation();
	const history = useHistory();
	// Memoize so context and hooks get a stable reference when search string is unchanged
	const queryParams = useMemo(
		() => decodeQueryStringValue(search),
		[search]
	);
	const persistedQuery = useSelector(
		(reduxState) => reduxState?.search?.query || null
	);

	const trackingPageType = {
		searchPage: PAGE_TYPES.SEARCH,
		cityLandingPage: PAGE_TYPES.CITY_LANDING,
		amenityLandingPage: PAGE_SUB_TYPES.AMENITY_LANDING
	};
	usePageViewTracking(trackingPageType[pageType] || PAGE_TYPES.SEARCH);

	const [showFilterModal, setShowFilterModal] = useState(false);
	const [showInlineFilters, setShowInlineFilters] = useState(false);
	const [isFiltering, setIsFiltering] = useState(false);
	const [isLoadingResults, setIsLoadingResults] = useState(false);
	const [showMapView, setShowMapView] = useState(false);
	const filtersContainerRef = useRef(null);
	const mapAndListRef = useRef(null);

	const {
		handleMapMove,
		handleMapLocationClick,
		showMapLoader,
		mapMoveMessage,
		searchFromMove,
		setSearchFromMove,
		selectedFacilityId,
		setSelectedFacilityId,
		hoveredFacilityId,
		setHoveredFacilityId,
		mapRef,
		googleMapsRef,
		pinSelected,
		setPinSelected,
		isOverlayClosing,
		setIsOverlayClosing
	} = useMapSearch();

	const handleShowMapView = () => {
		setShowMapView((v) => !v);
		setPinSelected(false);
		setSelectedFacilityId(null);
		setIsOverlayClosing(false);
	};

	useEffect(() => {
		setIsLoadingResults(false);
	}, [currentPage]);

	// Scroll to top whenever a new search completes (page 1). Does not run on "load more" (page 2+).
	const prevSearchLoadingRef = useRef(isSearchLoading);
	useEffect(() => {
		const wasLoading = prevSearchLoadingRef.current;
		prevSearchLoadingRef.current = isSearchLoading;
		if (wasLoading && !isSearchLoading && currentPage === 1) {
			if (typeof window !== "undefined") {
				window.scrollTo({ top: 0, behavior: "smooth" });
			}
		}
	}, [isSearchLoading, currentPage]);

	// React Router already listens to browser Back/Forward and updates
	// location.search. Use that as the single trigger to refetch search
	// results so URL state and Redux state stay in sync.
	const lastSearchRef = useRef(search);
	useEffect(() => {
		// Skip on initial mount; only react to actual changes.
		if (lastSearchRef.current === search) {
			return;
		}
		lastSearchRef.current = search;

		const nextQueryParams = decodeQueryStringValue(search) || {};
		const hasParams = Object.keys(nextQueryParams).length > 0;
		const hasPersisted =
			persistedQuery && Object.keys(persistedQuery).length > 0;

		// If we've navigated back to a URL with no query params but we
		// have a persisted server query (initial landing search), refetch
		// using that persisted query so the UI stays consistent and still
		// shows the loading state. Strip filter/sort params so we don't
		// re-apply stale filters from the previous search.
		if (!hasParams && hasPersisted) {
			const filterKeys = [...Object.values(FILTER_GROUPS), 'sort'];
			const baseQuery = Object.fromEntries(
				Object.entries(persistedQuery).filter(([k]) => !filterKeys.includes(k))
			);
			setIsFiltering(true);
			dispatch(
				getSearchPage({
					query: baseQuery
				})
			);
			return;
		}

		// If we have neither URL params nor a persisted query, there's
		// nothing meaningful to refetch.
		if (!hasParams && !hasPersisted) {
			setIsFiltering(false);
			return;
		}

		// Normal case: URL has params, refetch based on URL.
		setIsFiltering(true);
		dispatch(
			getSearchPage({
				query: nextQueryParams
			})
		);
	}, [search, dispatch, persistedQuery]);

	useEffect(() => {
		// When the search request completes (filters or sort applied),
		// clear the filtering state so loaders/animations stop and
		// scroll the results container back to the top.
		// isFiltering is intentionally excluded from deps: the effect should only
		// fire on isSearchLoading transitions, not when isFiltering is first set to
		// true — otherwise it clears immediately before the API call starts.
		if (!isSearchLoading && isFiltering) {
			setIsFiltering(false);

			if (typeof window !== "undefined") {
				const mainEl = document.querySelector(
					".search-page .full-page-scrollable .main"
				);
				if (mainEl) {
					mainEl.scrollTop = 0;
				} else {
					window.scrollTo({ top: 0, behavior: "smooth" });
				}
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isSearchLoading]);

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (
				filtersContainerRef.current &&
				!filtersContainerRef.current.contains(event.target)
			) {
				setShowInlineFilters(false);
			}
		};

		const handleKeyDown = (event) => {
			if (event.key === "Escape") {
				setShowInlineFilters(false);
			}
		};

		if (showInlineFilters) {
			document.addEventListener("mousedown", handleClickOutside);
			document.addEventListener("keydown", handleKeyDown);
		}

		return () => {
			if (showInlineFilters) {
				document.removeEventListener("mousedown", handleClickOutside);
				document.removeEventListener("keydown", handleKeyDown);
			}
		};
	}, [showInlineFilters]);

	// Deselect facility when user clicks outside map and list
	useEffect(() => {
		const handleClickOutsideMapAndList = (event) => {
			if (!selectedFacilityId) return;
			if (
				mapAndListRef.current &&
				!mapAndListRef.current.contains(event.target)
			) {
				setPinSelected(false);
				setSelectedFacilityId(null);
				setIsOverlayClosing(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutsideMapAndList);
		return () =>
			document.removeEventListener("mousedown", handleClickOutsideMapAndList);
	}, [selectedFacilityId, setPinSelected, setSelectedFacilityId, setIsOverlayClosing]);

	useEffect(() => {
		if (redirectTo) {
			navigate(redirectTo);
			dispatch(clearRedirectTo());
		}
	}, [redirectTo, dispatch]);

	// Sync URL path to API canonical/city when they differ; preserve amenity segments.
	useEffect(() => {
		const targetPath = getSearchPageSyncTargetPath({
			pathname,
			cityUrl,
			canonical: meta?.canonical
		});
		if (!targetPath) return;
		const hash = typeof window !== "undefined" ? (window.location.hash || "#searchPage") : "#searchPage";
		history.replace(`${targetPath}${search || ""}${hash}`);
	}, [cityUrl, pathname, search, history, meta?.canonical]);

	const {
		handleFilterChange,
		searchFilters,
		applySearch,
		selectedSort,
		handleSortChange
	} = useSearchFilters(
		[FILTER_GROUPS.UNIT_SIZE, FILTER_GROUPS.AMENITIES],
		queryParams,
		{ useClientSideSearch: true }
	);

	const activeFilterCount = useActiveFilterCount(searchFilters);

	const searchPageContext = useMemo(
		() => ({
			showFilterModal,
			setShowFilterModal,
			setIsFiltering,
			isFiltering,
			location,
			queryParams,
			searchFilters,
			handleFilterChange,
			applySearch,
			selectedSort,
			handleSortChange,
			totalListings,
			pageType,
			isCallCenterOpen,
			showInlineFilters,
			setShowInlineFilters,
			filtersContainerRef
		}),
		[
			showFilterModal,
			setShowFilterModal,
			setIsFiltering,
			isFiltering,
			location,
			queryParams,
			searchFilters,
			handleFilterChange,
			applySearch,
			selectedSort,
			handleSortChange,
			totalListings,
			pageType,
			isCallCenterOpen,
			showInlineFilters,
			setShowInlineFilters,
			filtersContainerRef
		]
	);

	const loadMore = useCallback(() => {
		setIsLoadingResults(true);
		const params =
			pageType !== "searchPage"
				? {
					...queryParams,
					location: `${preferredPlaceName}, ${state}`,
					city: preferredPlaceName,
					state
				}
				: queryParams;
		dispatch(getNextSearchResultsPage({ ...params, page: currentPage }));
	}, [
		dispatch,
		pageType,
		queryParams,
		preferredPlaceName,
		state,
		currentPage
	]);

	const showSearchLandingContent =
		(nearbyCities && nearbyCities.length > 0) || amenityPages;

	let newHeading = "Find Storage Units";

	if (pathname?.includes("climate-control-storage-units")) {
		newHeading = "Find Climate Controlled Storage";
	}
	if (pathname?.includes("twenty-four-hour-access-storage-units")) {
		newHeading = "Find 24 Hour Storage Units";
	}
	if (pathname?.includes("drive-up-storage-units")) {
		newHeading = "Find Drive Up Storage Units";
	}

	const stickToolBarProps = {
		headingTitle: `${newHeading} in`,
		filterCount: activeFilterCount,
		location: `${city}, ${state}`
	};

	const isCLP = pageType === "cityLandingPage" || pageType === "amenityLandingPage";
	const currentFacilityCount = facilityCards.length;

	const hasValidCenter = location?.latitude && location?.longitude;
	const mapCenter = hasValidCenter
		? { latitude: location.latitude, longitude: location.longitude }
		: null;

	// TODO : reuse this schema for other pages and move to a more global utils file 
	const breadcrumbItems = [
		{
			label: "Home",
			href: "/",
			name: "Home"
		}
	];

	if (stateUrl && location?.fullState) {
		breadcrumbItems.push({
			label: location.fullState,
			name: location.fullState,
			href: stateUrl
		});
	}

	if (cityUrl && preferredPlaceName) {
		breadcrumbItems.push({
			label: preferredPlaceName,
			name: preferredPlaceName,
			href: cityUrl
		});
	}

	const breadcrumbListSchema = {
		"@context": "https://schema.org",
		"@type": "BreadcrumbList",
		"@id": `${meta?.canonical}#breadcrumb`,
		itemListElement: breadcrumbItems
			.filter((crumb) => crumb.label)
			.map((crumb, index) => ({
				"@type": "ListItem",
				position: index + 1,
				item: {
					"@id": `${baseUrl}${crumb.href}`,
					name: crumb.name || crumb.label,
					url: `${baseUrl}${crumb.href}`
				}
			}))
	};

	const clpWebPageSchema = generateCLPWebPageSchema({
		city,
		state,
		canonicalUrl: meta?.canonical,
		title: meta?.title,
		description: meta?.description
	});
	const clpWebSiteSchema = generateCLPWebsiteSchema({
		baseUrl
	});

	const facilitySchemas = facilityCards.map(facility => {
		const schema = facility.schema || {};
		const amenityFeature = mapAmenitiesToFeature({
			amenities: schema?.amenities || {}
		});

		const paymentAccepted = schema?.paymentAccepted;
		const openingHours = schema?.openingHours || schema?.openingHoursSpecification;
		const sameAs = Array.isArray(schema?.sameAs) ? schema.sameAs : undefined;

		const baseStorageSchema = createSelfStorageSchema({
			...schema,
			amenityFeature,
			staticMapUrl: null,
			paymentAccepted,
			openingHours,
			sameAs
		});

		const { latitude, longitude } = schema?.location || {};
		return {
			...baseStorageSchema,
			"@id": `${schema?.url}#selfstorage`,
			priceRange: "$ - $$",
			hasMap: latitude && longitude
				? `https://www.google.com/maps?q=${latitude},${longitude}`
				: undefined
		};
	});

	const allSchemas = [
		organizationSchema,
		breadcrumbListSchema,
		clpWebPageSchema,
		clpWebSiteSchema,
		...facilitySchemas
	];


	const CombinedSchemaComponent = combineSchemas(allSchemas);

	// TODO: check if we can parse text to H2 in SEO-Service itself
	const updatedPageText = pageText ? pageText.replace("Finding storage", "Finding Storage Units")
		.replace(/<h3>/g, "<h2>")
		.replace(/<\/h3>/g, "</h2>")
		.replace(/<h4>/g, "<h3>")
		.replace(/<\/h4>/g, "</h3>") : '';

	const selectedCardForOverlay = useMemo(
		() =>
			pinSelected && selectedFacilityId
				? facilityCards.find((c) => c.id === selectedFacilityId) ?? null
				: null,
		[facilityCards, pinSelected, selectedFacilityId]
	);

	const onOverlayTransitionEnd = useCallback(
		(e) => {
			if (isOverlayClosing && e.propertyName === "transform") {
				setPinSelected(false);
				setSelectedFacilityId(null);
				setIsOverlayClosing(false);
			}
		},
		[isOverlayClosing, setPinSelected, setSelectedFacilityId, setIsOverlayClosing]
	);

	const showStickToolBar = totalListings == null || totalListings >= 0;

	return (
		<Page
			className={classnames("search-page-v2", {
				"search-page-v2--no-toolbar": !showStickToolBar
			})}
			id="searchPage"
			title={meta.title}
			pageType={pageType}
			description={meta.description}
			canonical={meta.canonical}
			noIndex={meta.noindex}
			isCLP={isCLP}
			isSearchPage
		>
			<SearchPageContext.Provider value={searchPageContext}>
				{CombinedSchemaComponent}
				{showStickToolBar && <StickToolBar {...stickToolBarProps} />}
				<div>
					<div className="full-page-scrollable">
						<div ref={mapAndListRef}>
							<Row
								className="page-layout"
								gutter="xlarge"
							>
								<SearchPageV2MapColumn
									showMapView={showMapView}
									mapCenter={mapCenter}
									facilityCards={facilityCards}
									handleMapMove={handleMapMove}
									handleMapLocationClick={handleMapLocationClick}
									showMapLoader={showMapLoader}
									mapMoveMessage={mapMoveMessage}
									searchFromMove={searchFromMove}
									setSearchFromMove={setSearchFromMove}
									selectedFacilityId={selectedFacilityId}
									hoveredFacilityId={hoveredFacilityId}
									setHoveredFacilityId={setHoveredFacilityId}
									mapRef={mapRef}
									googleMapsRef={googleMapsRef}
									selectedCardForOverlay={selectedCardForOverlay}
									isOverlayClosing={isOverlayClosing}
									onOverlayTransitionEnd={onOverlayTransitionEnd}
									locationName={preferredPlaceName && state ? `${preferredPlaceName}, ${state}` : preferredPlaceName}
								/>

								<Column
									className={`main ${showMapView ? "hide-list-view" : "show-list-view"}`}
									small={12}
									medium={5}
									mediumLarge={6}
									gutter="xlarge"
								>
									<div className="main-scroll-inner">

										<SearchResults
											facilityCards={facilityCards}
											currentPage={currentPage}
											selectedFacilityId={selectedFacilityId}
											hoveredFacilityId={hoveredFacilityId}
											onFacilityHover={setHoveredFacilityId}
											renderFacilitySchema={!isCLP}
										/>

										{hasMorePages && (
											<Button
												theme={
													isLoadingResults
														? "tertiary"
														: "secondary"
												}
												onClick={loadMore}
												className="load-more-button"
											>
												{isLoadingResults ? (
													<LoadingBoxes
														size="huge"
														primaryColor="white"
														secondaryColor="gray"
													/>
												) : (
													`Load ${currentFacilityCount >
														totalListings - 20
														? totalListings -
														currentFacilityCount
														: 20
													} More`
												)}
											</Button>
										)}
									</div>
								</Column>
							</Row>
						</div>
					</div>

					<Container className="search-page-content">
						<SearchBar
							buttonText="Search"
							id="bottom-search-bar"
							label="Enter ZIP code..."
							theme="lightBlue"
						>
							<Text
								size="large"
								weight='bold'
							>
								Update Your Storage Search</Text>
							<Paragraph
								last
								size="small"
							>
								Find storage units near you
							</Paragraph>
						</SearchBar>

						{updatedPageText && (
							<HTMLContent
								className="seoContent"
								content={updatedPageText}
							/>
						)}

						{showSearchLandingContent && (
							<Section className="search-landing-content">
								{amenityPages && (
									<SisterPages
										header={`Browse ${getDisplayCityName(preferredPlaceName)} Storage Units by Amenity`}
										cityName={getDisplayCityName(preferredPlaceName)}
										links={amenityPages}
									/>
								)}
								{nearbyCities?.length > 0 && (
									<NearbyCities nearbyCities={nearbyCities} location={location} />
								)}
							</Section>
						)}
						<Breadcrumbs
							location={location}
							cityUrl={cityUrl}
							stateUrl={stateUrl}
							isCLP={isCLP}
							canonicalUrl={meta?.canonical}
							isSearchPage
						/>
					</Container>
				</div>

				<ClickToCallTray
					isMapView={showMapView}
					onToggleMapView={handleShowMapView}
				/>

				{(((showMapLoader && searchFromMove && isSearchLoading) || isFiltering)) && (
					<div
						className="search-page-map-loader-overlay"
						aria-hidden="false"
						aria-busy="true"
						aria-label="Loading new results"
					>
						<LoadingBoxes
							size="huge"
							primaryColor="blue"
							secondaryColor="gray"
						/>
					</div>
				)}
			</SearchPageContext.Provider>
			<ScrollToTop />
		</Page>
	);
}
